import { Component, ElementRef, inject, Input, OnDestroy, OnInit } from '@angular/core'
import { ProjectCMSWidget, ScreenResolution } from 'htv-lib'
import { Subscription } from 'rxjs'
import { PageService } from '../../services/page.service'
import { PAGE_KEY } from '../../constants/page.constants'

@Component({
  selector: 'app-base-widget',
  standalone: true,
  imports: [],
  template: '',
})
export class BaseWidgetComponent implements OnInit, OnDestroy {
  private uid = `mc-${Math.random().toString(36).slice(2, 8)}`
  private styleEl!: HTMLStyleElement
  private rules = new Map<string, Record<string, string | number>>()

  subscriptions = new Subscription()

  @Input() config!: ProjectCMSWidget

  visibility = true
  enableVisibilityControl = false

  screenResolution = ScreenResolution.RES_1080P
  resolutionScale = 1.0

  protected el = inject(ElementRef)
  protected pageService = inject(PageService)

  constructor() {
    // do nothing
  }

  ngOnInit(): void {
    this.el.nativeElement.setAttribute('data-uid', this.uid)
    this.styleEl = document.createElement('style')
    this.styleEl.setAttribute('uid', this.uid)
    this.el.nativeElement.appendChild(this.styleEl)

    this.initResolutionScaling()
    this.applyContainerStyles()
    this.applyStyles()
    this.renderRules()
    this.registerVisibilityListener()
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }

  applyContainerStyles(): void {
    const containerStyle: Record<string, string | number> = {
      width: `${this.scaleToResolution(this.config.width)}px`,
      height: `${this.scaleToResolution(this.config.height)}px`,
      left: `${this.scaleToResolution(this.config.x)}px`,
      top: `${this.scaleToResolution(this.config.y)}px`,
    }
    if (this.config.padding != undefined) {
      containerStyle['padding'] = `${this.scaleToResolution(this.config.padding)}px`
    }
    if (this.config.backgroundColor) {
      containerStyle['background-color'] = this.config.backgroundColor
    }

    this.setRule('.container', containerStyle)
  }

  applyStyles(): void {
    // do nothing
  }

  setRule(selector: string, styles: Record<string, string | number>): void {
    const existingRules = this.rules.get(selector) ?? {}
    this.rules.set(selector, { ...existingRules, ...styles })
  }

  renderRules(): void {
    this.styleEl.textContent = Array.from(this.rules.entries())
      .map(([selector, styles]) => {
        const props = Object.entries(styles)
          .map(([prop, val]) => `${prop}: ${val} !important`)
          .join('; ')
        return `[data-uid="${this.uid}"] ${selector} { ${props} }`
      })
      .join('\n')
  }

  registerVisibilityListener(): void {
    if (this.enableVisibilityControl) {
      this.subscriptions.add(
        this.pageService.getSubject(PAGE_KEY.OVERLAY_VISIBILITY)?.subscribe((visibility: boolean) => {
          if (visibility != undefined) {
            this.visibility = visibility
            const opacity = this.visibility ? 1 : 0
            this.setRule('.container', { opacity: opacity })
            this.renderRules()
          }
        })
      )
    }
  }

  initResolutionScaling(): void {
    switch (this.screenResolution) {
      case ScreenResolution.RES_720P:
        this.resolutionScale = 2 / 3
        break
      case ScreenResolution.RES_1080P:
        this.resolutionScale = 1.0
        break
      case ScreenResolution.RES_4K:
        this.resolutionScale = 2.0
        break
    }
  }

  scaleToResolution(value: number): number {
    return Math.round(value * this.resolutionScale)
  }
}
